import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Modal, Table } from 'react-bootstrap';
import { PharmacyAlertsBar, PharmacyAlertsDoughnut, PharmacyChartCard } from './PharmacyAlertsCharts';
import {
  doughnutChartData,
  horizontalBarChartData,
  truncateLabel
} from '../../head-doctor/reports/charts/chartDataHelpers';
import { formatChartNumber, formatChartPercent } from '../../head-doctor/reports/charts/chartTheme';
import { EmptyBlock, LoadingBlock, formatNumber } from '../ui';
import { PHARMACY_COPY } from '../pharmacyCopy';
import { CENTRAL_PHARMACY_PATHS, PHARMACIST_PATHS } from '../pharmacyPaths';
import { useDispensationRoutes } from './dispensationUi';
import { formatReportDate, formatReportMoney } from '../utils/reportFormat';
import pharmacyService from '../../../services/pharmacyService';

const copy = PHARMACY_COPY.REPORTS;

const TOP_TEAL = ['#0f766e', '#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4', '#0f766e', '#115e59', '#134e4a', '#042f2e'];
const LEAST_AMBER = ['#b45309', '#d97706', '#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#92400e', '#78350f', '#a16207', '#ca8a04'];
const STOCK_RING = ['#0f766e', '#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4', '#115e59', '#134e4a', '#94a3b8'];

const barValueLabelsPlugin = {
  id: 'cpMedBarValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const horizontal = chart.options.indexAxis === 'y';
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;
      meta.data.forEach((el, index) => {
        const value = Number(dataset.data[index]);
        if (!Number.isFinite(value)) return;
        const { x, y } = el.getProps(['x', 'y'], true);
        ctx.save();
        ctx.fillStyle = '#0f172a';
        ctx.font = '700 11px system-ui, -apple-system, sans-serif';
        ctx.textBaseline = 'middle';
        const label = formatChartNumber(value);
        if (horizontal) {
          ctx.textAlign = 'left';
          ctx.fillText(label, x + 8, y);
        } else {
          ctx.textAlign = 'center';
          ctx.fillText(label, x, y - 10);
        }
        ctx.restore();
      });
    });
  }
};


function buildMedBarData(rows, valueKey, label, colors) {
  if (!rows?.length) return null;
  return horizontalBarChartData(
    rows.map((r) => truncateLabel(r.medication_name, 28)),
    rows.map((r) => Number(r[valueKey] || 0)),
    label,
    colors.slice(0, rows.length)
  );
}

function buildStockSegments(rows, maxItems = 8) {
  if (!rows?.length) return [];
  const sorted = [...rows].sort((a, b) => Number(b.on_hand || 0) - Number(a.on_hand || 0));
  const head = sorted.slice(0, maxItems);
  const tail = sorted.slice(maxItems);
  const total = sorted.reduce((sum, row) => sum + Number(row.on_hand || 0), 0);
  const totalValue = sorted.reduce((sum, row) => sum + Number(row.stock_value || 0), 0);
  const segments = head.map((row, index) => {
    const onHand = Number(row.on_hand || 0);
    const stockValue = Number(row.stock_value || 0);
    return {
      key: `med-${row.medication_id}`,
      medication_id: row.medication_id,
      medication_name: row.medication_name,
      unit_name: row.unit_name || '',
      on_hand: onHand,
      stock_value: stockValue,
      share_pct: total > 0 ? (onHand / total) * 100 : 0,
      value_share_pct: totalValue > 0 ? (stockValue / totalValue) * 100 : 0,
      color: STOCK_RING[index % STOCK_RING.length],
      isOthers: false
    };
  });
  const othersQty = tail.reduce((sum, row) => sum + Number(row.on_hand || 0), 0);
  const othersValue = tail.reduce((sum, row) => sum + Number(row.stock_value || 0), 0);
  if (othersQty > 0) {
    segments.push({
      key: 'others',
      medication_id: null,
      medication_name: copy.CHART_STOCK_OTHERS,
      unit_name: '',
      on_hand: othersQty,
      stock_value: othersValue,
      share_pct: total > 0 ? (othersQty / total) * 100 : 0,
      value_share_pct: totalValue > 0 ? (othersValue / totalValue) * 100 : 0,
      color: '#cbd5e1',
      isOthers: true
    });
  }
  return segments;
}

function groupsForMedication(groups, medicationId) {
  return (groups || [])
    .map((group) => {
      const matched = (group.items || []).filter(
        (item) => String(item.medication_id) === String(medicationId)
      );
      if (!matched.length) return null;
      const matchedQty = matched.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      return { ...group, matched_items: matched, matched_qty: matchedQty };
    })
    .filter(Boolean);
}

function MetricToggle({ value, onChange }) {
  return (
    <div className="cp-filter-pills cp-reports-med-metric" role="group" aria-label={copy.METRIC_ARIA}>
      <button
        type="button"
        className={`cp-filter-pill${value === 'prescription_count' ? ' is-active' : ''}`}
        onClick={() => onChange('prescription_count')}
        aria-pressed={value === 'prescription_count'}
      >
        {copy.METRIC_RX}
      </button>
      <button
        type="button"
        className={`cp-filter-pill${value === 'total_quantity' ? ' is-active' : ''}`}
        onClick={() => onChange('total_quantity')}
        aria-pressed={value === 'total_quantity'}
      >
        {copy.METRIC_QTY}
      </button>
    </div>
  );
}

function ClickableStat({ value, onClick, ariaLabel, className = '' }) {
  return (
    <button
      type="button"
      className={`cp-reports-stat-btn ${className}`.trim()}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {formatNumber(value)}
    </button>
  );
}

function MedRankTable({ rows, onOpenMed }) {
  return (
    <div className="cp-table-wrap">
      <Table responsive className="cp-table mb-0">
        <thead>
          <tr>
            <th>#</th>
            <th>{copy.COL_MED}</th>
            <th className="text-end">{copy.COL_RX_COUNT}</th>
            <th className="text-end">{copy.COL_TOTAL_QTY}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.medication_id}
              className="cp-reports-med-row"
              onClick={() => onOpenMed(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenMed(row);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={copy.MED_OPEN_ARIA.replace('{name}', row.medication_name)}
            >
              <td className="cp-num text-muted">{idx + 1}</td>
              <td>
                <strong>{row.medication_name}</strong>
                {row.unit_name ? <span className="text-muted small ms-1">{row.unit_name}</span> : null}
              </td>
              <td className="text-end">
                <ClickableStat
                  value={row.prescription_count}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenMed(row);
                  }}
                  ariaLabel={copy.MED_OPEN_RX_ARIA.replace('{name}', row.medication_name)}
                />
              </td>
              <td className="text-end">
                <ClickableStat
                  value={row.total_quantity}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenMed(row);
                  }}
                  ariaLabel={copy.MED_OPEN_QTY_ARIA.replace('{name}', row.medication_name)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function MedBarCard({ title, subtitle, rows, metric, colors, onPickIndex }) {
  const valueKey = metric;
  const axisLabel = metric === 'total_quantity' ? copy.CHART_AXIS_QTY : copy.CHART_AXIS_COUNT;
  const chartData = useMemo(
    () => buildMedBarData(rows, valueKey, axisLabel, colors),
    [rows, valueKey, axisLabel, colors]
  );
  const height = Math.max(280, rows.length * 38 + 72);

  const chartOptions = useMemo(
    () => ({
      interaction: { mode: 'nearest', intersect: true, axis: 'y' },
      onHover(event, elements) {
        const native = event?.native?.target;
        if (native) native.style.cursor = elements?.length ? 'pointer' : 'default';
      },
      onClick(_event, elements) {
        if (!elements?.length) return;
        onPickIndex(elements[0].index);
      },
      layout: { padding: { right: 36 } },
      scales: {
        x: {
          grace: '12%',
          ticks: { precision: 0 }
        },
        y: {
          ticks: { font: { size: 11, weight: '600' } }
        }
      },
      plugins: {
        tooltip: {
          callbacks: {
            afterBody(items) {
              const idx = items?.[0]?.dataIndex;
              const row = rows[idx];
              if (!row) return [];
              return [
                `${copy.COL_RX_COUNT} : ${formatNumber(row.prescription_count)}`,
                `${copy.COL_TOTAL_QTY} : ${formatNumber(row.total_quantity)}${row.unit_name ? ` ${row.unit_name}` : ''}`
              ];
            }
          }
        }
      }
    }),
    [onPickIndex, rows]
  );

  return (
    <PharmacyChartCard title={title} subtitle={subtitle} height={height} className="cp-reports-med-chart">
      <PharmacyAlertsBar
        data={chartData}
        horizontal
        xLabel={axisLabel}
        options={chartOptions}
        plugins={[barValueLabelsPlugin]}
      />
    </PharmacyChartCard>
  );
}

function StockCamembert({ segments, activeKey, onSelect }) {
  const chartData = useMemo(() => {
    if (!segments.length) return null;
    const data = doughnutChartData(
      segments.map((s) => truncateLabel(s.medication_name, 22)),
      segments.map((s) => s.on_hand),
      segments.map((s) => s.color)
    );
    if (data?.datasets?.[0]) {
      data.datasets[0].hoverOffset = 12;
      data.datasets[0].borderWidth = 3;
      data.datasets[0].borderColor = '#ffffff';
    }
    return data;
  }, [segments]);

  const pickSegment = useCallback(
    (index) => {
      const segment = segments[index];
      if (!segment || segment.isOthers) return;
      onSelect(segment);
    },
    [onSelect, segments]
  );

  const chartOptions = useMemo(
    () => ({
      cutout: '62%',
      layout: { padding: 8 },
      interaction: { mode: 'nearest', intersect: true },
      onHover(event, elements) {
        const native = event?.native?.target;
        if (native) native.style.cursor = elements?.length ? 'pointer' : 'default';
      },
      onClick(_event, elements) {
        if (!elements?.length) return;
        pickSegment(elements[0].index);
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          displayColors: true,
          callbacks: {
            title(items) {
              const segment = segments[items?.[0]?.dataIndex];
              return segment?.medication_name || '';
            },
            label(ctx) {
              const segment = segments[ctx.dataIndex];
              if (!segment) return '';
              const lines = [
                `${copy.COL_ON_HAND} : ${formatChartNumber(segment.on_hand)}`,
                `${copy.CHART_STOCK_SHARE} : ${formatChartPercent(segment.share_pct)}`
              ];
              if (segment.stock_value > 0) {
                lines.push(`${copy.CHART_STOCK_VALUE} : ${formatReportMoney(segment.stock_value)}`);
              }
              return lines;
            }
          }
        }
      }
    }),
    [pickSegment, segments]
  );

  if (!segments.length) {
    return <EmptyBlock label={copy.EMPTY_STOCK} />;
  }

  return (
    <div className="cp-stock-camembert">
      <div className="cp-stock-camembert__body">
        <div className="cp-stock-camembert__chart">
          <PharmacyAlertsDoughnut data={chartData} cutout="62%" options={chartOptions} />
        </div>

        <ul className="cp-stock-camembert__legend" aria-label={copy.CHART_STOCK_TITLE}>
          {segments.map((segment) => {
            const isActive = activeKey === segment.key;
            const interactive = !segment.isOthers;
            return (
              <li key={segment.key}>
                <button
                  type="button"
                  className={`cp-stock-camembert__item${isActive ? ' is-active' : ''}${interactive ? '' : ' is-muted'}`}
                  onClick={() => interactive && onSelect(segment)}
                  disabled={!interactive}
                  aria-label={
                    interactive
                      ? copy.STOCK_OPEN_QTY_ARIA.replace('{name}', segment.medication_name).replace(
                          '{qty}',
                          formatNumber(segment.on_hand)
                        )
                      : undefined
                  }
                >
                  <span className="cp-stock-camembert__swatch" style={{ background: segment.color }} />
                  <span className="cp-stock-camembert__name">
                    {segment.medication_name}
                    {segment.unit_name ? (
                      <span className="cp-stock-camembert__unit"> {segment.unit_name}</span>
                    ) : null}
                  </span>
                  <span className="cp-stock-camembert__pct">{formatChartPercent(segment.share_pct, 0)}</span>
                  <span className="cp-stock-camembert__qty">
                    {interactive ? (
                      <ClickableStat
                        value={segment.on_hand}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(segment);
                        }}
                        ariaLabel={copy.STOCK_OPEN_QTY_ARIA.replace('{name}', segment.medication_name).replace(
                          '{qty}',
                          formatNumber(segment.on_hand)
                        )}
                      />
                    ) : (
                      <span className="cp-num">{formatNumber(segment.on_hand)}</span>
                    )}
                  </span>
                  {segment.stock_value > 0 ? (
                    <span className="cp-stock-camembert__money">
                      {interactive ? (
                        <button
                          type="button"
                          className="cp-reports-stat-btn cp-reports-stat-btn--money"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(segment);
                          }}
                          aria-label={(copy.STOCK_OPEN_VALUE_ARIA || copy.STOCK_OPEN_QTY_ARIA)
                            .replace('{name}', segment.medication_name)
                            .replace('{value}', formatReportMoney(segment.stock_value))
                            .replace('{qty}', formatNumber(segment.on_hand))}
                        >
                          {formatReportMoney(segment.stock_value)}
                        </button>
                      ) : (
                        <span className="cp-num">{formatReportMoney(segment.stock_value)}</span>
                      )}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default function PharmacyReportsMedicationsSection({
  topMeds = [],
  leastMeds = [],
  topInStock = [],
  groups = [],
  siteId = ''
}) {
  const routes = useDispensationRoutes();
  const { pathname } = useLocation();
  const stockPath = pathname.startsWith('/pharmacist')
    ? PHARMACIST_PATHS.SITE_STOCKS
    : CENTRAL_PHARMACY_PATHS.SITE_STOCKS;

  const [metric, setMetric] = useState('total_quantity');
  const [selected, setSelected] = useState(null);
  const [stockSelected, setStockSelected] = useState(null);
  const [stockLots, setStockLots] = useState([]);
  const [stockLotsLoading, setStockLotsLoading] = useState(false);
  const [stockLotsError, setStockLotsError] = useState(null);

  const stockSegments = useMemo(() => buildStockSegments(topInStock, 8), [topInStock]);

  const topRows = useMemo(() => {
    const rows = [...topMeds];
    if (metric === 'total_quantity') {
      rows.sort((a, b) => {
        if (b.total_quantity !== a.total_quantity) return b.total_quantity - a.total_quantity;
        return b.prescription_count - a.prescription_count;
      });
    }
    return rows;
  }, [topMeds, metric]);

  const leastRows = useMemo(() => {
    const rows = [...leastMeds];
    if (metric === 'total_quantity') {
      rows.sort((a, b) => {
        if (a.total_quantity !== b.total_quantity) return a.total_quantity - b.total_quantity;
        return a.prescription_count - b.prescription_count;
      });
    }
    return rows;
  }, [leastMeds, metric]);

  const openMed = useCallback((row) => {
    if (!row) return;
    setSelected(row);
  }, []);

  const closeMed = useCallback(() => setSelected(null), []);

  const openStock = useCallback((segmentOrRow) => {
    if (!segmentOrRow || segmentOrRow.isOthers || !segmentOrRow.medication_id) return;
    setStockSelected({
      medication_id: segmentOrRow.medication_id,
      medication_name: segmentOrRow.medication_name,
      unit_name: segmentOrRow.unit_name || '',
      on_hand: Number(segmentOrRow.on_hand || 0),
      stock_value: Number(segmentOrRow.stock_value || 0),
      key: segmentOrRow.key || `med-${segmentOrRow.medication_id}`
    });
  }, []);

  const closeStock = useCallback(() => {
    setStockSelected(null);
    setStockLots([]);
    setStockLotsError(null);
  }, []);

  useEffect(() => {
    if (!stockSelected?.medication_id) return undefined;
    let cancelled = false;
    (async () => {
      setStockLotsLoading(true);
      setStockLotsError(null);
      try {
        const res = await pharmacyService.getMedicationLots(stockSelected.medication_id, {
          ...(siteId ? { site_id: siteId } : {})
        });
        if (cancelled) return;
        const lots = res?.success ? res.data || [] : [];
        setStockLots(Array.isArray(lots) ? lots.filter((lot) => Number(lot.quantity || 0) > 0) : []);
      } catch {
        if (!cancelled) {
          setStockLots([]);
          setStockLotsError(copy.STOCK_LOTS_ERROR);
        }
      } finally {
        if (!cancelled) setStockLotsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId, stockSelected]);

  const detailGroups = useMemo(
    () => (selected ? groupsForMedication(groups, selected.medication_id) : []),
    [groups, selected]
  );

  const pickTop = useCallback(
    (index) => {
      openMed(topRows[index]);
    },
    [openMed, topRows]
  );

  const pickLeast = useCallback(
    (index) => {
      openMed(leastRows[index]);
    },
    [openMed, leastRows]
  );

  return (
    <>
      <div className="cp-reports-med-toolbar">
        <p className="cp-reports-med-toolbar__hint mb-0">{copy.MED_CHART_HINT}</p>
        <MetricToggle value={metric} onChange={setMetric} />
      </div>

      <section className="cp-alerts-charts cp-reports-med-charts">
        <div className="cp-alerts-charts__row cp-alerts-charts__row--meds">
          <MedBarCard
            title={copy.CHART_TOP_MED_TITLE}
            subtitle={
              metric === 'total_quantity' ? copy.CHART_TOP_MED_DESC_QTY : copy.CHART_TOP_MED_DESC
            }
            rows={topRows}
            metric={metric}
            colors={TOP_TEAL}
            onPickIndex={pickTop}
          />
          <MedBarCard
            title={copy.CHART_LEAST_MED_TITLE}
            subtitle={
              metric === 'total_quantity' ? copy.CHART_LEAST_MED_DESC_QTY : copy.CHART_LEAST_MED_DESC
            }
            rows={leastRows}
            metric={metric}
            colors={LEAST_AMBER}
            onPickIndex={pickLeast}
          />
        </div>

        <article className="cp-card cp-reports-stock-panel">
          <div className="cp-reports-section-head cp-reports-stock-panel__head">
            <div>
              <h2 className="cp-reports-section-head__title mb-0">{copy.CHART_STOCK_TITLE}</h2>
              <p className="cp-reports-section-head__meta mb-0">{copy.CHART_STOCK_HINT}</p>
            </div>
            <Link className="cp-link" to={stockPath}>
              {copy.STOCK_GO_TO_STOCK}
            </Link>
          </div>
          <div className="cp-card__body">
            <StockCamembert
              segments={stockSegments}
              activeKey={stockSelected?.key}
              onSelect={openStock}
            />
          </div>
        </article>
      </section>

      <div className="cp-reports-med-grid">
        <div className="cp-card">
          <div className="cp-reports-section-head">
            <h2 className="cp-reports-section-head__title mb-0">{copy.CHART_TOP_MED_TITLE}</h2>
            <p className="cp-reports-section-head__meta mb-0">{copy.MED_TABLE_HINT}</p>
          </div>
          <div className="cp-card__body cp-card__body--flush">
            {topRows.length === 0 ? (
              <EmptyBlock label={copy.EMPTY_MEDICATIONS} />
            ) : (
              <MedRankTable rows={topRows} onOpenMed={openMed} />
            )}
          </div>
        </div>

        <div className="cp-card">
          <div className="cp-reports-section-head">
            <h2 className="cp-reports-section-head__title mb-0">{copy.CHART_LEAST_MED_TITLE}</h2>
            <p className="cp-reports-section-head__meta mb-0">{copy.MED_TABLE_HINT}</p>
          </div>
          <div className="cp-card__body cp-card__body--flush">
            {leastRows.length === 0 ? (
              <EmptyBlock label={copy.EMPTY_MEDICATIONS} />
            ) : (
              <MedRankTable rows={leastRows} onOpenMed={openMed} />
            )}
          </div>
        </div>
      </div>

      <Modal show={!!selected} onHide={closeMed} size="lg" centered className="cp-modal">
        <Modal.Header closeButton className="cp-modal__header">
          <Modal.Title as="h2" className="cp-modal__title">
            {selected?.medication_name || copy.MED_DETAIL_TITLE}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          {selected ? (
            <>
              <div className="cp-reports-med-detail__stats" aria-label={copy.MED_DETAIL_STATS_ARIA}>
                <div>
                  <span className="cp-reports-med-detail__label">{copy.COL_RX_COUNT}</span>
                  <strong>{formatNumber(selected.prescription_count)}</strong>
                </div>
                <div>
                  <span className="cp-reports-med-detail__label">{copy.COL_TOTAL_QTY}</span>
                  <strong>
                    {formatNumber(selected.total_quantity)}
                    {selected.unit_name ? ` ${selected.unit_name}` : ''}
                  </strong>
                </div>
              </div>

              {detailGroups.length === 0 ? (
                <EmptyBlock label={copy.MED_DETAIL_EMPTY} />
              ) : (
                <div className="cp-table-wrap">
                  <Table responsive className="cp-table mb-0">
                    <thead>
                      <tr>
                        <th>{copy.COL_DATE}</th>
                        <th>{copy.COL_NUMBER}</th>
                        <th>{copy.COL_PATIENT}</th>
                        <th>{copy.COL_COMPANY}</th>
                        <th className="text-end">{copy.COL_TOTAL_QTY}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {detailGroups.map((g) => (
                        <tr key={g.dispensation_id}>
                          <td className="cp-num">{formatReportDate(g.event_date)}</td>
                          <td className="cp-num">{g.prescription_number || g.dispensation_id}</td>
                          <td>{g.patient_display || '—'}</td>
                          <td>{g.company_name || '—'}</td>
                          <td className="text-end cp-num">{formatNumber(g.matched_qty)}</td>
                          <td className="text-end">
                            <Link
                              className="cp-link"
                              to={routes.DETAIL(g.dispensation_id)}
                              onClick={closeMed}
                            >
                              {copy.MED_OPEN_DISPENSATION}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-btn cp-btn--ghost" onClick={closeMed}>
            {copy.MED_DETAIL_CLOSE}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!stockSelected} onHide={closeStock} size="lg" centered className="cp-modal">
        <Modal.Header closeButton className="cp-modal__header">
          <Modal.Title as="h2" className="cp-modal__title">
            {stockSelected?.medication_name || copy.STOCK_DETAIL_TITLE}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          {stockSelected ? (
            <>
              <div className="cp-reports-med-detail__stats" aria-label={copy.STOCK_DETAIL_STATS_ARIA}>
                <div>
                  <span className="cp-reports-med-detail__label">{copy.COL_ON_HAND}</span>
                  <strong>
                    {formatNumber(stockSelected.on_hand)}
                    {stockSelected.unit_name ? ` ${stockSelected.unit_name}` : ''}
                  </strong>
                </div>
                <div>
                  <span className="cp-reports-med-detail__label">{copy.CHART_STOCK_VALUE}</span>
                  <strong>
                    {stockSelected.stock_value > 0
                      ? formatReportMoney(stockSelected.stock_value)
                      : '—'}
                  </strong>
                </div>
                <div>
                  <span className="cp-reports-med-detail__label">{copy.COL_LOT}</span>
                  <strong>{stockLotsLoading ? '…' : formatNumber(stockLots.length)}</strong>
                </div>
              </div>

              {stockLotsLoading ? (
                <LoadingBlock label={copy.STOCK_LOTS_LOADING} />
              ) : stockLotsError ? (
                <EmptyBlock label={stockLotsError} />
              ) : stockLots.length === 0 ? (
                <EmptyBlock label={copy.EMPTY_STOCK_LOTS} />
              ) : (
                <div className="cp-table-wrap">
                  <Table responsive className="cp-table mb-0">
                    <thead>
                      <tr>
                        <th>{copy.COL_LOT}</th>
                        <th>{copy.COL_SITE}</th>
                        <th>{copy.COL_EXPIRY}</th>
                        <th className="text-end">{copy.COL_ON_HAND}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockLots.map((lot) => (
                        <tr key={lot.id}>
                          <td className="cp-num">{lot.lot_number || lot.id}</td>
                          <td>{lot.site_name || '—'}</td>
                          <td className="cp-num">{formatReportDate(lot.expiry_date)}</td>
                          <td className="text-end cp-num">{formatNumber(lot.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <Link className="cp-btn cp-btn--ghost" to={stockPath} onClick={closeStock}>
            {copy.STOCK_GO_TO_STOCK}
          </Link>
          <button type="button" className="cp-btn cp-btn--primary" onClick={closeStock}>
            {copy.STOCK_DETAIL_CLOSE}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
